import { useRef, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { HexColorPicker } from "react-colorful";
import { Bold, Italic, Underline as UnderlineIcon, Link as LinkIcon, Palette, RemoveFormatting } from "lucide-react";

const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return { r: 0, g: 0, b: 0 };
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const clamp = (n) => Math.max(0, Math.min(255, parseInt(n || 0, 10) || 0));
const rgbToHex = ({ r, g, b }) => "#" + [r, g, b].map((v) => clamp(v).toString(16).padStart(2, "0")).join("");

export const RichTextEditor = ({ value, onChange, multiline = true, placeholder = "", testid }) => {
  const ref = useRef(null);
  const savedRange = useRef(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkTarget, setLinkTarget] = useState("new");
  const [colorOpen, setColorOpen] = useState(false);
  const [color, setColor] = useState("#F4F0EA");
  const [fmt, setFmt] = useState({ bold: false, italic: false, underline: false, link: false });
  const editedAnchor = useRef(null);

  // The <a> the caret/selection currently sits in, if any.
  const currentAnchor = () => {
    const el = ref.current;
    const sel = window.getSelection();
    if (!el || !sel || !sel.anchorNode || !el.contains(sel.anchorNode)) return null;
    let n = sel.anchorNode;
    while (n && n !== el) {
      if (n.nodeType === 1 && n.tagName === "A") return n;
      n = n.parentNode;
    }
    return null;
  };

  const refreshFmt = () => {
    const el = ref.current;
    if (!el || document.activeElement !== el) return;
    setFmt({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      link: !!currentAnchor(),
    });
  };

  useEffect(() => {
    document.addEventListener("selectionchange", refreshFmt);
    return () => document.removeEventListener("selectionchange", refreshFmt);
  }, []);

  // Sync external value in only when the editor isn't focused (avoids caret jumps)
  useEffect(() => {
    const el = ref.current;
    if (!el || document.activeElement === el) return;
    const clean = DOMPurify.sanitize(value || "", { ADD_ATTR: ["target", "rel"] });
    if (clean !== el.innerHTML) el.innerHTML = clean;
  }, [value]);

  // Chrome's insertHTML copies the editor's computed font-size/background onto new
  // nodes, which would shrink the text on the public site. Strip those.
  const stripInheritedStyles = (root) => {
    root.querySelectorAll("[style]").forEach((el) => {
      ["font-size", "background-color", "background", "font-family", "line-height"].forEach((p) =>
        el.style.removeProperty(p)
      );
      if (!el.getAttribute("style")) el.removeAttribute("style");
    });
  };

  const emit = () => {
    if (!ref.current) return onChange("");
    stripInheritedStyles(ref.current);
    onChange(ref.current.innerHTML);
  };

  const focusEditor = () => ref.current && ref.current.focus();

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };
  const restoreSelection = () => {
    const sel = window.getSelection();
    sel.removeAllRanges();
    if (savedRange.current) sel.addRange(savedRange.current);
  };

  const exec = (cmd, val = null) => {
    focusEditor();
    document.execCommand(cmd, false, val);
    emit();
    refreshFmt();
  };

  const openLink = () => {
    saveSelection();
    const a = currentAnchor();
    editedAnchor.current = a;
    setLinkUrl(a ? a.getAttribute("href") || "" : "");
    setLinkTarget(a ? (a.getAttribute("target") === "_blank" ? "new" : "same") : "new");
    setColorOpen(false);
    setLinkOpen((o) => !o);
  };

  const selectNode = (node) => {
    const r = document.createRange();
    r.selectNodeContents(node);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(r);
    savedRange.current = r.cloneRange();
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    const a = editedAnchor.current;

    // Empty input on an existing link → remove the link, keep the text.
    if (!url) {
      if (a && ref.current && ref.current.contains(a)) {
        const parent = a.parentNode;
        const frag = document.createDocumentFragment();
        while (a.firstChild) frag.appendChild(a.firstChild);
        const first = frag.firstChild;
        parent.replaceChild(frag, a);
        focusEditor();
        if (first) selectNode(first.nodeType === 1 ? first : first.parentNode);
        emit();
        refreshFmt();
      }
      editedAnchor.current = null;
      setLinkOpen(false);
      return;
    }

    const href = /^https?:\/\//i.test(url) || /^mailto:/i.test(url) || url.startsWith("/") ? url : "https://" + url;

    // Editing an existing link → just update it in place.
    if (a && ref.current && ref.current.contains(a)) {
      a.setAttribute("href", href);
      if (linkTarget === "new") { a.setAttribute("target", "_blank"); a.setAttribute("rel", "noopener noreferrer"); }
      else { a.removeAttribute("target"); a.removeAttribute("rel"); }
      focusEditor();
      selectNode(a);
      emit();
      refreshFmt();
      editedAnchor.current = null;
      setLinkOpen(false);
      return;
    }

    focusEditor();
    restoreSelection();
    const sel = window.getSelection();
    const text = sel && sel.toString() ? sel.toString() : href;
    const attrs = linkTarget === "new" ? ' target="_blank" rel="noopener noreferrer"' : "";
    document.execCommand("insertHTML", false, `<a href="${href}"${attrs}><u>${text}</u></a>`);
    emit();
    refreshFmt();
    setLinkOpen(false);
  };

  const openColor = () => {
    saveSelection();
    setLinkOpen(false);
    setColorOpen((o) => !o);
  };

  const applyColor = () => {
    focusEditor();
    restoreSelection();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand("foreColor", false, color);
    emit();
    setColorOpen(false);
  };

  const rgb = hexToRgb(color);
  const setRgb = (key, v) => setColor(rgbToHex({ ...rgb, [key]: clamp(v) }));

  const Btn = ({ onClick, title, active, children, tid }) => (
    <button type="button" title={title} data-testid={tid} data-active={active ? "true" : "false"}
      onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className={`w-8 h-8 flex items-center justify-center border transition-colors ${
        active
          ? "bg-[var(--vsl-fg)] text-[var(--vsl-bg)] border-[var(--vsl-fg)]"
          : "border-[var(--vsl-border)] text-[var(--vsl-fg)] hover:border-[var(--vsl-fg)] hover:bg-white/5"
      }`}>
      {children}
    </button>
  );

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-1 mb-2">
        <Btn tid={`${testid}-regular`} title="Regular (clear formatting)" onClick={() => { exec("removeFormat"); exec("unlink"); }}><RemoveFormatting size={14} /></Btn>
        <Btn tid={`${testid}-bold`} title="Bold" active={fmt.bold} onClick={() => exec("bold")}><Bold size={14} /></Btn>
        <Btn tid={`${testid}-italic`} title="Italic" active={fmt.italic} onClick={() => exec("italic")}><Italic size={14} /></Btn>
        <Btn tid={`${testid}-underline`} title="Underline" active={fmt.underline} onClick={() => exec("underline")}><UnderlineIcon size={14} /></Btn>
        <Btn tid={`${testid}-link`} title="Add link" active={fmt.link} onClick={openLink}><LinkIcon size={14} /></Btn>
        <Btn tid={`${testid}-color`} title="Text colour" onClick={openColor}><Palette size={14} /></Btn>
      </div>

      <div
        ref={ref}
        data-testid={testid}
        contentEditable
        suppressContentEditableWarning
        onInput={() => { emit(); refreshFmt(); }}
        onKeyUp={refreshFmt}
        onMouseUp={refreshFmt}
        onFocus={refreshFmt}
        onBlur={emit}
        data-placeholder={placeholder}
        className="vsl-rte w-full min-h-[64px] bg-transparent border border-[var(--vsl-border)] px-3 py-2.5 font-mono text-sm outline-none focus:border-[var(--vsl-fg)] leading-relaxed whitespace-pre-wrap"
      />

      {linkOpen && (
        <div data-testid={`${testid}-link-popover`} className="absolute z-30 mt-1 w-72 bg-[var(--vsl-bg)] border border-[var(--vsl-border)] p-3 shadow-2xl space-y-3">
          <input autoFocus value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://example.com"
            data-testid={`${testid}-link-url`}
            className="w-full bg-transparent border border-[var(--vsl-border)] px-2 py-2 font-mono text-xs outline-none focus:border-[var(--vsl-fg)]" />
          <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-wider text-[var(--vsl-muted)]">
            <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" name={`${testid}-tgt`} checked={linkTarget === "same"} onChange={() => setLinkTarget("same")} /> Same window</label>
            <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" name={`${testid}-tgt`} checked={linkTarget === "new"} onChange={() => setLinkTarget("new")} /> New window</label>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setLinkOpen(false)} className="font-mono text-[10px] uppercase tracking-wider text-[var(--vsl-muted)] hover:text-[var(--vsl-fg)] px-2 py-1.5">Cancel</button>
            <button type="button" onClick={applyLink} data-testid={`${testid}-link-apply`} className="bg-[var(--vsl-fg)] text-[var(--vsl-bg)] font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 hover:bg-[var(--vsl-limestone)]">
              {editedAnchor.current ? (linkUrl.trim() ? "Update link" : "Remove link") : "Add link"}
            </button>
          </div>
        </div>
      )}

      {colorOpen && (
        <div data-testid={`${testid}-color-popover`} className="absolute z-30 mt-1 w-64 bg-[var(--vsl-bg)] border border-[var(--vsl-border)] p-3 shadow-2xl space-y-3">
          <HexColorPicker color={color} onChange={setColor} style={{ width: "100%", height: 140 }} />
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded border border-[var(--vsl-border)] shrink-0" style={{ backgroundColor: color }} />
            <div className="flex items-center border border-[var(--vsl-border)] px-2">
              <span className="font-mono text-xs text-[var(--vsl-muted)]">#</span>
              <input value={color.replace(/^#/, "")} maxLength={6} spellCheck="false"
                onChange={(e) => { const v = e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6); setColor("#" + v); }}
                data-testid={`${testid}-color-hex`}
                className="w-16 bg-transparent py-2 font-mono text-xs outline-none uppercase" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {["r", "g", "b"].map((k) => (
              <div key={k} className="flex items-center gap-1 border border-[var(--vsl-border)] px-2">
                <span className="font-mono text-[10px] uppercase text-[var(--vsl-muted)]">{k}</span>
                <input type="number" min={0} max={255} value={rgb[k]} onChange={(e) => setRgb(k, e.target.value)}
                  data-testid={`${testid}-color-${k}`}
                  className="w-full bg-transparent py-1.5 font-mono text-xs outline-none" />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setColorOpen(false)} className="font-mono text-[10px] uppercase tracking-wider text-[var(--vsl-muted)] hover:text-[var(--vsl-fg)] px-2 py-1.5">Cancel</button>
            <button type="button" onClick={applyColor} data-testid={`${testid}-color-apply`} className="bg-[var(--vsl-fg)] text-[var(--vsl-bg)] font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 hover:bg-[var(--vsl-limestone)]">Apply colour</button>
          </div>
        </div>
      )}
    </div>
  );
};
